import type React from 'react';
import { useCallback, useState } from 'react';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu, MenuButton, MenuItem, MenuProvider } from '@ariakit/react/menu';
import { SwarmIconClasses, SwarmType, SwarmTypeTranslations } from '../../../../../enums';
import classNames from '../../../../../utils/classNames';
import Button, { VARIANT_CLASSES } from '../../../../form/Button';

const PROJECT_SWARM_TYPES = [SwarmType.Vibration, SwarmType.Air, SwarmType.Sound] as const;

export function CreateProjectMenu(): React.ReactElement {
    const [open, setOpen] = useState(false);

    const closeOnBlur = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
        const nextFocusedElement = event.relatedTarget;
        if (
            !(nextFocusedElement instanceof Node) ||
            !event.currentTarget.contains(nextFocusedElement)
        ) {
            setOpen(false);
        }
    }, []);

    return (
        <MenuProvider
            open={open}
            setOpen={setOpen}
            placement="bottom-end"
            showTimeout={100}
            hideTimeout={0}
        >
            <div className="relative inline-flex" onBlur={closeOnBlur}>
                <MenuButton
                    className={classNames(
                        VARIANT_CLASSES.primary,
                        'inline-flex items-center gap-2 px-6 py-2.5 text-sm md:text-base'
                    )}
                    aria-label={gettext('CREATE_PROJECT')}
                >
                    <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
                    {gettext('CREATE_PROJECT')}
                </MenuButton>
                <Menu
                    gutter={8}
                    overflowPadding={8}
                    className="z-floating-menu shadow-floating-menu w-90 overflow-hidden rounded-sm border border-gray-200 bg-white"
                >
                    {PROJECT_SWARM_TYPES.map((swarmType) => (
                        <MenuItem
                            key={swarmType}
                            className="block w-full border-b border-gray-200 p-0 transition-colors last:border-b-0 hover:bg-gray-100 data-[active-item]:bg-gray-100"
                            render={<Button variant="base" to={`/project/create/${swarmType}`} />}
                        >
                            <span className="flex items-center justify-between px-5 py-3.5 text-left">
                                <span className="text-base text-gray-700">
                                    {SwarmTypeTranslations[swarmType]}
                                </span>
                                <span className="flex items-center gap-3">
                                    <span
                                        className={classNames(
                                            SwarmIconClasses[swarmType],
                                            'inline-block h-7 w-7 bg-contain bg-center bg-no-repeat'
                                        )}
                                        aria-hidden
                                    />
                                </span>
                            </span>
                        </MenuItem>
                    ))}
                </Menu>
            </div>
        </MenuProvider>
    );
}
